/**
 * How one screen-share track is produced to the SFU.
 *
 * Moved out of `useMeetingConnection` unchanged, so a share from the meeting
 * and a share from the Screen Share page reach viewers encoded the same way:
 * one encoding, held resolution, tagged `sourceType: 'screen'`.
 */
import type { Producer, Transport } from 'mediasoup-client/types'
import { vclog } from '../hooks/vc-log'

/**
 * Ceiling for a screen share's single encoding.
 *
 * Higher than a camera's top rung (1.2 Mbps) on purpose: a share is usually the
 * thing everyone is actually reading, it is the one video we never pause on
 * visibility, and text goes to mush long before motion does.
 */
export const SCREEN_SHARE_MAX_BITRATE = 2_500_000

export async function produceScreenTrack(transport: Transport, track: MediaStreamTrack): Promise<Producer> {
  // Video only. getDisplayMedia is asked for system audio too, so when the
  // user grants it there is an audio track wearing the same 'screen'
  // sourceType, and video encodings must not be put on it.
  const isVideo = track.kind === 'video'

  // 'detail': a shared screen is for reading. Under bandwidth or CPU
  // pressure the encoder now holds resolution and drops frames, instead of
  // the other way round.
  //
  // This was 'motion' from 2026-09-08, chosen for the H.323 presentation
  // leg, where 'detail' delivered ~10-12 fps against 30 and scrolling looked
  // stepped. The cost was the reverse, and it landed on every viewer: each
  // time the bandwidth estimate dipped, Chrome scaled the share down and
  // text went blurry until the screen stopped changing — "the shared screen
  // is intermittently blurred" for host and participants alike. Readable
  // text is the point of a screen share, and it is what other meeting apps
  // default to; motion-heavy content is the case that gives a little.
  if (isVideo && 'contentHint' in track) track.contentHint = 'detail'

  // Deliberately a single encoding: simulcast on a screen share triples the
  // encode cost of the thing least able to spare it, and everyone is looking
  // at the same one large copy anyway.
  //
  // `networkPriority: 'medium'` is the other half of shielding the mic and
  // camera (see useLocalMedia.ts): it tells Chrome's estimator to give this
  // stream up first, not an equal share, when a screen share starting or
  // stopping forces it to rebalance bitrate across the shared transport.
  const producer = await transport.produce({
    track,
    encodings: isVideo
      ? [{ maxBitrate: SCREEN_SHARE_MAX_BITRATE, networkPriority: 'medium' }]
      : [{ networkPriority: 'medium' }],
    appData: { sourceType: 'screen' },
  })
  // What actually got negotiated — the codec is never pinned for a share
  // (see the encodings above), so this is the only place that says whether
  // the router handed it H264 or VP8, and single-encoding is confirmed
  // (not inferred) by `encodings.length`.
  if (isVideo) {
    const codec = producer.rtpParameters.codecs[0]
    vclog('producer', 'screen share negotiated', {
      codec: codec?.mimeType,
      codecParameters: codec?.parameters,
      encodings: producer.rtpParameters.encodings,
      simulcast: (producer.rtpParameters.encodings?.length ?? 0) > 1,
    })
  }
  // The hint sets the encoder's default; say it on the sender as well,
  // where browsers that honour `degradationPreference` take it as the rule.
  // Best effort — older browsers reject the field and keep the hint alone.
  if (isVideo && producer.rtpSender) {
    try {
      const params = producer.rtpSender.getParameters()
      params.degradationPreference = 'maintain-resolution'
      await producer.rtpSender.setParameters(params)
    } catch (err) {
      vclog('producer', 'screen share: degradationPreference not applied', { error: (err as Error).message })
    }
  }
  return producer
}
